import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

import OutsideOfDeadlineStep from '../common/OutsideOfDeadlineStep';
import container from '../../../../container';
import Loader from '../../../common/Loader';
import LoadingError from '../../../common/LoadingError';
import { StepProps } from '../types';

function ManualDeadlineCheckStep(props: StepProps) {
  const { shopId, claim, as: StepContainer, onNext, onChange } = props;

  const { data, isFetching, isFetchedAfterMount, isError } = useQuery({
    queryKey: ['get-deadline-and-requirements', shopId],
    queryFn: async () => {
      const [deadlineResponse, claimRequirements] = await Promise.all([
        container.touchpointsApi.postClaimsDeadlines(shopId, {
          claimType: claim.type,
          ...claim,
        }),

        container.touchpointsApi.postClaimRequirements(shopId, {
          carrierCode: claim.carrierCode || '',
          carrierCountryCode: claim.carrierCountryCode || '',
          deliveryDate: claim.deliveryDate ? claim.deliveryDate.toISOString() : null,
          postalReturnDeliveryDate: claim.postalReturnDeliveryDate
            ? claim.postalReturnDeliveryDate.toISOString()
            : null,
          deliveredToMailboxDate: claim.deliveredToMailboxDate ? claim.deliveredToMailboxDate.toISOString() : null,
          isReturnShipment: claim.isReturnShipment || false,
          isMultiShipmentOrder: claim.isMultiShipmentOrder ?? null,
        }),
      ]);

      return {
        manualDeadline: deadlineResponse.deadline,
        claimRequirements,
      };
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: unknown
  useEffect(() => {
    if (isFetchedAfterMount && data) {
      const isWithinDeadline = data.manualDeadline === null || data.manualDeadline?.outcome === 'within';

      const requirementsPatch = {
        claimRequirements: data.claimRequirements,
        isOrderItemTaxRate: claim.shopSettings.isOrderItemLevelTaxEnabled,
      };

      // we need to reset deadlines form if user goes back and changes something which makes new deadline to pass
      const patch = isWithinDeadline
        ? {
            ...requirementsPatch,
            outsideOfDeadlineReason: null,
            deadlineSupportingDocuments: [],
          }
        : requirementsPatch;

      onChange(patch);

      if (isWithinDeadline) {
        // we don't want navigation history to remember this step because when deadlines within we just skip it
        // pass patch via context so state machine guards see fresh claimRequirements (setClaim batched, closure stale)
        onNext && onNext({ replace: true, context: patch });
      }
    }
  }, [data, isFetchedAfterMount]);

  if (isFetching) {
    return (
      <StepContainer {...props}>
        <Loader />
      </StepContainer>
    );
  }

  if (isError) {
    return (
      <StepContainer {...props}>
        <LoadingError />
      </StepContainer>
    );
  }

  return <OutsideOfDeadlineStep manualDeadline={data?.manualDeadline} {...props} />;
}

export default React.memo(ManualDeadlineCheckStep);
